import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { ReactComponent as MarkerRed } from 'assets/icons/marker.svg'
import { ReactComponent as MarkerBlack } from 'assets/icons/marker-black.svg'
import StudentNotesModal from 'components/modals/StudentNotesModal'
import {
  ContrastMode,
  contrastColorsByMode,
  DEFAULT_CONTRAST_MODE
} from 'config/theme'

import Cookies from 'js-cookie'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { selectCurrentClassroom } from 'app/reducer/studentSlice'
import { setThemeContrast } from 'app/reducer/themeSlice'
import { QuestionnaireContext } from 'components/Assessment/QuestionnaireContext'
import SelectionBox from 'components/SelectionBox'
import {
  bindDrawing,
  bindEraser,
  eraseDrawing,
  getMarkings,
  initDrawing,
  setMarkings,
  unbindDrawing,
  unbindEraser,
  updatePaperSize
} from 'util/RaphaelInit'
import { useParams } from 'react-router-dom'
import { showDesmosCalculator } from 'util/getCalculator'
import {
  FooterIconContainer,
  MarkerTrigger,
  FooterHighlighterIcon,
  FooterContrastIcon,
  FooterCalculatorIcon,
  FooterNotesIcon,
  HighlighterPopupWrapper,
  HighlighterPopupContainer,
  PopupHighlighterIcon,
  MarkerPopupWrapper,
  MarkerPopupContainer,
  PopupRedDotIcon,
  PopupBlackDotIcon,
  PopupEraserIcon,
  ClearAllButton,
  PopupRemoveIcon,
  ContrastPopupWrapper,
  ContrastTrigger,
  ContrastPopupContainer,
  ContrastCardGrid,
  ContrastCard,
  CalculatorPopupWrapper,
  CalculatorPopupContainer,
  CalculatorOptionButton,
  CalculatorTrigger,
  NotesTrigger
} from './styles'
import {
  destroyTiCalculator,
  showTiCalculator,
  TiCalculatorInstance
} from 'util/showTiCalculator'
import {
  SelectAssignmentState,
  updateAssignmentPageAction
} from 'app/reducer/assignmentSlice'
import { useUpdateStudentContrastPresetMutation } from 'services/studentApi'
import { upsertAssignmentAnnotations } from 'services/assignments.api'

let calculator: any = null
const TI84_CALCULATOR_MOUNT_ID = 'my_calculator'

/**
 * Returns the active Desmos calculator instance used by StudentTools.
 * This shared module variable is used so open/close handlers can access
 * the same calculator object across helper functions.
 */
export const getCalculator = () => {
  return calculator
}

/**
 * Persists the current Desmos calculator instance for reuse or teardown.
 * Passing null clears the stored instance after destroy/close operations.
 */
export const setCalculator = (calc: any) => {
  calculator = calc
}

type ContrastOption = {
  id: ContrastMode
  label: string
}

type StudentToolsProps = {
  contrastVisible?: boolean
  markerVisible?: boolean
  highlighterVisible?: boolean
  calculatorVisible?: boolean
  notesVisible?: boolean
}

/**
 * Ordered list of contrast presets shown in the contrast popup.
 * IDs map directly to theme contrast modes in redux/theme config.
 */
const CONTRAST_OPTIONS: ContrastOption[] = [
  {
    id: 'black_on_white',
    label: 'Black on White'
  },
  {
    id: 'white_on_black',
    label: 'White on Black'
  },
  {
    id: 'dark_blue_on_gold',
    label: 'Dark Blue on Gold'
  },
  {
    id: 'yellow_on_red',
    label: 'Yellow on Red'
  },
  {
    id: 'yellow_on_dark_green',
    label: 'Yellow on Dark Green'
  },
  {
    id: 'light_pink_on_dark_blue',
    label: 'Light Pink on Dark Blue'
  },
  {
    id: 'black_on_cream',
    label: 'Black on Cream'
  },
  {
    id: 'dark_blue_on_light_blue',
    label: 'Dark Blue on Light Blue'
  },
  {
    id: 'yellow_on_dark_blue',
    label: 'Yellow on Dark Blue'
  },
  {
    id: 'black_on_yellow',
    label: 'Black on Yellow'
  }
]

const StudentTools = ({
  contrastVisible = true,
  markerVisible = true,
  highlighterVisible = true,
  calculatorVisible = true,
  notesVisible = true
}: StudentToolsProps) => {
  /**
   * Redux dispatch used for tool actions that modify global UI state,
   * such as contrast mode updates.
   */
  const dispatch = useAppDispatch()
  const [updateStudentContrastPreset] = useUpdateStudentContrastPresetMutation()
  /**
   * Context-provided values for tool coordination with preview content.
   * Includes view mode, highlighter state setter, calculator host lookup,
   * and grade needed for the Desmos calculator configuration.
   */
  const {
    setShowHighlightEraser,
    showHighlightEraser,
    setIsHighlighterActive,
    getCalculatorElement,
    grade,
    questionIdx,
    questions,
    onChange,
    currentQuestion
  } = useContext(QuestionnaireContext)
  const { assessmentType } = useParams()
  const currentClassroom = useAppSelector(selectCurrentClassroom)
  const { pageIdx, assignment, currentPage } = useAppSelector(
    SelectAssignmentState
  )
  const isAssignment = assessmentType === 'assignments'

  /**
   * Calculator visibility comes only from its matching permissions: standard
   * calculator enables Desmos and TI-84 enables the TI-84 calculator.
   */
  const isTi84CalculatorAllowed = isAssignment
    ? Boolean(
        Cookies.get('allow_ti84_calculator') === 'true' ||
        currentClassroom?.allow_ti84_calculator ||
        assignment?.allow_ti84_calculator
      )
    : Boolean(
        Cookies.get('allow_ti84_calculator') === 'true' ||
        currentClassroom?.allow_ti84_calculator
      )

  const isDesmosCalculatorAllowed = isAssignment
    ? Boolean(
        currentClassroom?.allow_calculator ||
        assignment?.allow_calculator ||
        Cookies.get('allow_calculator') === 'true'
      )
    : Boolean(
        currentClassroom?.allow_calculator ||
        Cookies.get('allow_calculator') === 'true'
      )

  /**
   * Graphing is available for grades 6 through 8. Grades 6 and 7 pair it with
   * four-function, grade 8 pairs it with scientific, and kindergarten grade 9
   * remains on the four-function calculator only.
   */
  const numericGrade = Number(grade)
  const isGraphingCalculatorAllowed =
    isTi84CalculatorAllowed &&
    numericGrade >= 6 &&
    numericGrade <= 8
  const allowCalculator =
    isGraphingCalculatorAllowed || isDesmosCalculatorAllowed

  /**
   * Selected contrast mode from global theme state.
   * Used to highlight the currently active contrast option card.
   */
  const selectedContrastId = useAppSelector(
    (state) => state.themeReducer.contrast
  )

  /**
   * Global student submission lock state.
   * When enabled, interactive tool actions are disabled.
   */
  const enableStudentSubmissionView = useAppSelector(
    (state) => state.assignmentReducer.enableStudentSubmissionView
  )

  /**
   * Assignment and Benchmark/Formative questions both support student notes, but the notes are stored differently.
   *  This derives the correct notes content and context label based on the active assessment type. The context label is used for accessibility and UI display in the notes modal.
   */
  const notesContextLabel = isAssignment
    ? `Page ${pageIdx + 1}`
    : `Question ${questionIdx + 1}`
  const totalQuestions = Math.max(1, questions.length)
  const notesSubtitle = isAssignment
    ? currentPage?.title || ''
    : `Questions 1 - ${totalQuestions}`
  const notesHtml = isAssignment
    ? currentPage?.student_notes || ''
    : (questions[questionIdx] as any)?.student_notes || ''
  const notesPenMarkings = isAssignment
    ? currentPage?.student_pen_note_markings || null
    : null

  /**
   * Single source of truth for which tool is currently active.
   * Includes marker, contrast, highlighter, calculator, or none.
   */
  const [activeTool, setActiveTool] = useState<
    'marker' | 'contrast' | 'highlighter' | 'calculator' | null
  >(null)

  /**
   * Per-tool local UI state used to control popups, modals, and marker mode.
   */
  const [isScreenMarked, setIsScreenMarked] = useState(false)
  const [isHighlighterPopupOpen, setIsHighlighterPopupOpen] = useState(false)
  const [isMarkerPopupOpen, setIsMarkerPopupOpen] = useState(false)
  const [isContrastPopupOpen, setIsContrastPopupOpen] = useState(false)
  const [isCalculatorPopupOpen, setIsCalculatorPopupOpen] = useState(false)
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false)
  const [notesDraft, setNotesDraft] = useState(notesHtml)
  const [selectedMarkerColor, setSelectedMarkerColor] = useState<
    'red' | 'black' | null
  >(null)

  useEffect(() => {
    if (!isNotesModalOpen) {
      setNotesDraft(notesHtml)
    }
  }, [isNotesModalOpen, notesHtml])

  /**
   * DOM refs for popup anchoring and drawing tool adapters.
   */
  const markerPopupRef = useRef<HTMLDivElement | null>(null)
  const highlighterPopupRef = useRef<HTMLDivElement | null>(null)
  const markerRef = useRef<any>(null)
  const eraserRef = useRef<any>(null)
  const contrastPopupRef = useRef<HTMLDivElement | null>(null)
  const calculatorPopupRef = useRef<HTMLDivElement | null>(null)
  const drawingInitializedRef = useRef(false)
  const markerAutosaveTimeoutRef = useRef<number | null>(null)
  const lastSavedMarkerMarkingsRef = useRef<string>('')
  const markerMarkingsByPageRef = useRef<Record<number, any[]>>({})
  const markerMarkingsByQuestionRef = useRef<Record<number, any[]>>({})
  const highlightsByQuestionRef = useRef<Record<number, string>>({})
  const highLighterRef = useRef<any>(null)
  const removeHighlightRef = useRef<any>(null)
  const currentQuestionId = Number(currentQuestion?.id)
  const currentQuestionIdRef = useRef(currentQuestionId)
  currentQuestionIdRef.current = currentQuestionId

  /**
   * Derived UI flags used by rendering and accessibility bindings.
   */
  const isMarkerActive = activeTool === 'marker'
  const getHighlightArea = () => document.getElementById('highlight')

  /**
   * Resolved visual/icon choices and keyboard behavior helpers.
   */
  const MarkerIcon = selectedMarkerColor === 'black' ? MarkerBlack : MarkerRed
  const interactiveTabIndex = enableStudentSubmissionView ? -1 : 0
  const isRedSelected = selectedMarkerColor === 'red'
  const isBlackSelected = selectedMarkerColor === 'black'
  const isCalculatorOpen = activeTool === 'calculator'
  /**
   * When both calculators are permitted for an eligible grade, show a chooser
   * with the grade-appropriate standard calculator label.
   */
  const shouldShowCalculatorChoicePopup =
    isGraphingCalculatorAllowed && isDesmosCalculatorAllowed
  const standardCalculatorLabel =
    numericGrade === 8 ? 'Scientific Calculator' : '4-Function Calculator'

  const resolvePenMarkingsPayload = useCallback((payload: any) => {
    if (!payload) {
      return []
    }

    let parsedPayload = payload

    // Support string, stringified JSON object, and double-stringified payloads.
    for (let parseAttempt = 0; parseAttempt < 2; parseAttempt += 1) {
      if (typeof parsedPayload !== 'string') {
        break
      }

      const trimmedPayload = parsedPayload.trim()
      if (!trimmedPayload) {
        return []
      }

      try {
        parsedPayload = JSON.parse(trimmedPayload)
      } catch {
        return []
      }
    }

    if (Array.isArray(parsedPayload)) {
      return parsedPayload
    }

    const parseArrayLikeValue = (value: any) => {
      if (Array.isArray(value)) {
        return value
      }

      if (typeof value === 'string') {
        const trimmedValue = value.trim()
        if (!trimmedValue) {
          return []
        }

        try {
          const reparsedValue = JSON.parse(trimmedValue)
          return Array.isArray(reparsedValue) ? reparsedValue : []
        } catch {
          return []
        }
      }

      return []
    }

    // Main expected shape: { markings: [...] }.
    const markingsFromKey = parseArrayLikeValue(parsedPayload?.markings)
    if (markingsFromKey.length) {
      return markingsFromKey
    }

    // Fallbacks for backend variants.
    const pathsFromKey = parseArrayLikeValue(parsedPayload?.paths)
    if (pathsFromKey.length) {
      return pathsFromKey
    }

    const annotationsFromKey = parseArrayLikeValue(parsedPayload?.annotations)
    if (annotationsFromKey.length) {
      return annotationsFromKey
    }

    return []
  }, [])

  const saveStudentPenMarkings = useCallback(
    async (nextMarkings: any[]) => {
      if (!isAssignment) {
        return
      }

      const classroomId = currentClassroom?.id
      const assignmentId = assignment?.id
      const pageId = currentPage?.id

      if (!classroomId || !assignmentId || !pageId || !currentPage) {
        return
      }

      markerMarkingsByPageRef.current[pageId] = nextMarkings || []

      const serializedNextMarkings = JSON.stringify(nextMarkings || null)

      if (serializedNextMarkings === lastSavedMarkerMarkingsRef.current) {
        return
      }

      const previousPage = currentPage

      dispatch(
        updateAssignmentPageAction({
          ...previousPage,
          student_pen_markings: nextMarkings
        })
      )

      lastSavedMarkerMarkingsRef.current = serializedNextMarkings

      try {
        await upsertAssignmentAnnotations(classroomId, assignmentId, {
          page_id: pageId,
          is_bookmarked: Boolean(previousPage?.is_bookmarked),
          student_pen_markings: serializedNextMarkings
        })
      } catch (error) {
        markerMarkingsByPageRef.current[pageId] = resolvePenMarkingsPayload(
          previousPage?.student_pen_markings
        )
        lastSavedMarkerMarkingsRef.current = JSON.stringify(
          previousPage?.student_pen_markings || null
        )
        dispatch(updateAssignmentPageAction(previousPage))
        console.error('Failed to save assignment marker annotations', error)
      }
    },
    [
      assignment?.id,
      currentClassroom?.id,
      currentPage,
      dispatch,
      isAssignment,
      resolvePenMarkingsPayload
    ]
  )

  const scheduleStudentPenMarkingsSave = useCallback(
    (nextMarkings?: any[]) => {
      if (!isAssignment || activeTool !== 'marker') {
        return
      }

      if (markerAutosaveTimeoutRef.current) {
        window.clearTimeout(markerAutosaveTimeoutRef.current)
      }

      markerAutosaveTimeoutRef.current = window.setTimeout(() => {
        const resolvedMarkings = nextMarkings || getMarkings()
        setIsScreenMarked((resolvedMarkings || []).length > 0)
        saveStudentPenMarkings(resolvedMarkings || [])
      }, 1000)
    },
    [activeTool, isAssignment, saveStudentPenMarkings]
  )

  /**
   * Assessment pen paths are copied into a question-scoped in-memory cache
   * after drawing or erasing. This also covers direct question jumps that do
   * not use the provider's next/back handlers.
   */
  const cacheCurrentQuestionPenMarkings = useCallback(() => {
    const questionId = currentQuestionIdRef.current
    if (isAssignment || !questionId) return

    window.requestAnimationFrame(() => {
      markerMarkingsByQuestionRef.current[questionId] = getMarkings()
    })
  }, [isAssignment])

  const handleScreenMarked = useCallback(
    (isMarked: boolean) => {
      setIsScreenMarked(isMarked)
      if (isMarked) {
        cacheCurrentQuestionPenMarkings()
      }
    },
    [cacheCurrentQuestionPenMarkings]
  )

  /**
   * Eraser click handler removes a pen path and refreshes its question cache.
   */
  const handler = useCallback(
    (event: any) => {
      const elem = event.target
      if (
        elem.nodeName === 'path' &&
        elem.parentNode.id === 'sketch-container'
      ) {
        elem.remove()
        cacheCurrentQuestionPenMarkings()
      }
    },
    [cacheCurrentQuestionPenMarkings]
  )

  const initializeDrawingSurface = useCallback(() => {
    const drawingContainer = document.getElementById('sketchContainerELA')
    if (!drawingContainer) return false

    drawingContainer.style.position = 'relative'

    if (drawingInitializedRef.current) {
      const hasAttachedCanvas = Boolean(
        drawingContainer.querySelector('svg,canvas')
      )

      if (hasAttachedCanvas) {
        const rect = drawingContainer.getBoundingClientRect()
        const width = Math.round(drawingContainer.clientWidth || rect.width)
        const height = Math.round(drawingContainer.clientHeight || rect.height)

        if (width && height) {
          updatePaperSize(width, height)
        }

        return true
      }

      // The content container can be remounted; force re-init when canvas is gone.
      drawingInitializedRef.current = false
    }

    const rect = drawingContainer.getBoundingClientRect()
    const width = Math.round(drawingContainer.clientWidth || rect.width)
    const height = Math.round(drawingContainer.clientHeight || rect.height)

    if (!width || !height) return false

    initDrawing(width, height)
    drawingInitializedRef.current = true
    return true
  }, [])

  /**
   * Initializes drawing engine for marker/eraser tools on mount,
   * then performs cleanup of listeners and drawing bindings on unmount.
   */
  useEffect(() => {
    if (!markerVisible) return

    if (!initializeDrawingSurface()) {
      const rafId = window.requestAnimationFrame(() => {
        initializeDrawingSurface()
      })

      return () => {
        window.cancelAnimationFrame(rafId)
        document.removeEventListener('click', handler)
        eraseDrawing()
        unbindDrawing()
        unbindEraser()
        drawingInitializedRef.current = false
      }
    }

    return () => {
      document.removeEventListener('click', handler)
      eraseDrawing()
      unbindDrawing()
      unbindEraser()
      drawingInitializedRef.current = false
    }
  }, [handler, markerVisible, initializeDrawingSurface])

  /**
   * Keeps local tool selection in sync when calculator is closed externally
   * by the shared overlay close button rendered at PreviewAssessmentV2 level.
   */
  useEffect(() => {
    const onExternalCalculatorClose = () => {
      setActiveTool((prev) => (prev === 'calculator' ? null : prev))
    }

    window.addEventListener('cms-calculator-close', onExternalCalculatorClose)

    return () => {
      window.removeEventListener(
        'cms-calculator-close',
        onExternalCalculatorClose
      )
    }
  }, [])

  /**
   * Binds marker drawing when a marker color is selected and unbinds when not.
   * This ensures drawing mode only runs while marker tool is actively configured.
   */
  useEffect(() => {
    if (isMarkerActive && (isRedSelected || isBlackSelected)) {
      initializeDrawingSurface()
      bindDrawing(handleScreenMarked, { markerColor: selectedMarkerColor })
    } else {
      markerRef.current && unbindDrawing()
    }
  }, [
    isMarkerActive,
    selectedMarkerColor,
    handleScreenMarked,
    isRedSelected,
    isBlackSelected,
    initializeDrawingSurface
  ])

  useEffect(() => {
    if (!isAssignment || !currentPage?.id) {
      return
    }

    const pageSavedMarkings = resolvePenMarkingsPayload(
      currentPage?.student_pen_markings
    )
    const cachedMarkings = markerMarkingsByPageRef.current[currentPage.id] || []
    const savedMarkings = pageSavedMarkings.length
      ? pageSavedMarkings
      : cachedMarkings

    const loadSavedMarkings = () => {
      if (!initializeDrawingSurface()) {
        return false
      }

      setMarkings(savedMarkings, null)
      setIsScreenMarked(savedMarkings.length > 0)
      lastSavedMarkerMarkingsRef.current = JSON.stringify(savedMarkings || null)
      return true
    }

    if (loadSavedMarkings()) {
      return
    }

    let isCancelled = false
    let retryTimeoutId: number | null = null

    const retryLoadSavedMarkings = () => {
      if (isCancelled) {
        return
      }

      if (loadSavedMarkings()) {
        return
      }

      retryTimeoutId = window.setTimeout(retryLoadSavedMarkings, 80)
    }

    retryLoadSavedMarkings()

    return () => {
      isCancelled = true
      if (retryTimeoutId) {
        window.clearTimeout(retryTimeoutId)
      }
    }
  }, [
    currentPage?.id,
    currentPage?.student_pen_markings,
    initializeDrawingSurface,
    isAssignment,
    resolvePenMarkingsPayload
  ])

  useEffect(() => {
    if (!isAssignment || !currentPage?.id) {
      return
    }

    const pageId = currentPage.id
    const pageMarkings = resolvePenMarkingsPayload(
      currentPage?.student_pen_markings
    )
    const cachedMarkings = markerMarkingsByPageRef.current[pageId] || []
    const resolvedMarkings = pageMarkings.length ? pageMarkings : cachedMarkings

    markerMarkingsByPageRef.current[pageId] = resolvedMarkings

    const serializedCurrent = JSON.stringify(pageMarkings || null)
    const serializedResolved = JSON.stringify(resolvedMarkings || null)

    if (serializedCurrent !== serializedResolved) {
      dispatch(
        updateAssignmentPageAction({
          ...currentPage,
          student_pen_markings: resolvedMarkings
        })
      )
    }
  }, [
    currentPage,
    currentPage?.id,
    currentPage?.student_pen_markings,
    dispatch,
    isAssignment,
    resolvePenMarkingsPayload
  ])

  useEffect(() => {
    if (!isAssignment) {
      return
    }

    const resolvedMarkings = resolvePenMarkingsPayload(
      currentPage?.student_pen_markings
    )

    lastSavedMarkerMarkingsRef.current = JSON.stringify(
      resolvedMarkings || null
    )
  }, [
    currentPage?.id,
    currentPage?.student_pen_markings,
    isAssignment,
    resolvePenMarkingsPayload
  ])

  useEffect(() => {
    if (!isAssignment || !isMarkerActive) {
      return
    }

    const markerCanvas = document.getElementById('sketch-container')
    if (!markerCanvas) {
      return
    }

    const handleMarkerInteractionEnd = () => {
      scheduleStudentPenMarkingsSave()
    }

    markerCanvas.addEventListener('mouseup', handleMarkerInteractionEnd)
    markerCanvas.addEventListener('touchend', handleMarkerInteractionEnd)
    markerCanvas.addEventListener('click', handleMarkerInteractionEnd)

    return () => {
      markerCanvas.removeEventListener('mouseup', handleMarkerInteractionEnd)
      markerCanvas.removeEventListener('touchend', handleMarkerInteractionEnd)
      markerCanvas.removeEventListener('click', handleMarkerInteractionEnd)
    }
  }, [isAssignment, isMarkerActive, scheduleStudentPenMarkingsSave])

  useEffect(() => {
    return () => {
      if (markerAutosaveTimeoutRef.current) {
        window.clearTimeout(markerAutosaveTimeoutRef.current)
      }
    }
  }, [])

  /**
   * Benchmark and formative highlights stay in memory for the active assessment
   * session. Serializing after each interaction keeps them scoped by question
   * without adding browser storage or an API payload.
   */
  const cacheCurrentQuestionHighlights = useCallback(() => {
    const questionId = currentQuestionIdRef.current
    if (isAssignment || !questionId) return

    const highlightArea = getHighlightArea()
    if (!highlightArea) return

    const activeHighlighter = [
      highLighterRef.current,
      removeHighlightRef.current
    ].find((highlighter) => highlighter?.el === highlightArea)
    const temporaryHighlighter = activeHighlighter
      ? null
      : new (window.TextHighlighter as any)(highlightArea as HTMLElement, {
          isRemoveHighlight: false,
          setIsScreenMarked
        })
    const highlighter = activeHighlighter || temporaryHighlighter
    const serializedHighlights = highlighter.serializeHighlights()

    highlightsByQuestionRef.current[questionId] = serializedHighlights
    temporaryHighlighter?.destroy()
  }, [isAssignment])

  /**
   * Activates text highlighter on the highlight surface.
   * Stores created highlighter instance in a ref for later teardown.
   */
  const onClickHighLighter = (setIsScreenMarked: Function) => {
    const highlightArea = getHighlightArea()

    if (highlightArea) {
      highLighterRef.current = new (window.TextHighlighter as any)(
        highlightArea as HTMLElement,
        {
          isRemoveHighlight: false,
          setIsScreenMarked,
          onAfterHighlight: () => {
            setIsScreenMarked(true)
            cacheCurrentQuestionHighlights()
          }
        }
      )
    }
  }

  /**
   * Activates highlighter eraser mode so highlighted text can be removed.
   */
  const onClickHighlighterEraser = useCallback(
    (setIsScreenMarked: Function) => {
      const highlightArea = getHighlightArea()

      if (highlightArea) {
        removeHighlightRef.current = new (window.TextHighlighter as any)(
          highlightArea as HTMLElement,
          {
            isRemoveHighlight: true,
            setIsScreenMarked,
            onAfterRemoveHighlight: () => {
              /**
               * Cache on the next frame after the DOM reflects the deletion.
               */
              window.requestAnimationFrame(cacheCurrentQuestionHighlights)
            }
          }
        )
      }
    },
    [cacheCurrentQuestionHighlights]
  )

  /**
   * Destroys active highlighter instance to stop highlight interactions.
   */
  const onDestroyHighlighter = useCallback(() => {
    const highlightArea = getHighlightArea()

    if (highlightArea) {
      highLighterRef?.current?.destroy()
    }
  }, [])

  /**
   * Destroys highlight remover instance used for highlight erasing mode.
   */
  const onDestroyHighlighterRemover = useCallback(() => {
    const highlightArea = getHighlightArea()

    if (highlightArea) {
      removeHighlightRef?.current?.destroy()
    }
  }, [])

  /**
   * V2 benchmark and formative navigation reuses the question-scoped pen paths
   * already held by QuestionnaireContext and restores serialized highlights
   * from this mounted tool instance. Both caches disappear on browser refresh.
   */
  useEffect(() => {
    if (isAssignment || !currentQuestionId) return

    const restoreQuestionMarkings = async () => {
      if (!initializeDrawingSurface()) return

      const penMarkings =
        markerMarkingsByQuestionRef.current[currentQuestionId] ||
        currentQuestion?.penMarkings ||
        []
      const serializedHighlights =
        highlightsByQuestionRef.current[currentQuestionId] || '[]'
      const highlightArea = getHighlightArea()

      setMarkings(penMarkings)

      if (highlightArea) {
        const activeHighlighter = [
          highLighterRef.current,
          removeHighlightRef.current
        ].find((highlighter) => highlighter?.el === highlightArea)
        const temporaryHighlighter = activeHighlighter
          ? null
          : new (window.TextHighlighter as any)(highlightArea as HTMLElement, {
              isRemoveHighlight: false,
              setIsScreenMarked
            })
        const highlighter = activeHighlighter || temporaryHighlighter

        await highlighter.removeHighlights()
        highlighter.deserializeHighlights(serializedHighlights)
        temporaryHighlighter?.destroy()
      }

      setIsScreenMarked(penMarkings.length > 0 || serializedHighlights !== '[]')
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      void restoreQuestionMarkings()
    })

    return () => {
      window.cancelAnimationFrame(animationFrameId)
    }
  }, [
    currentQuestion?.penMarkings,
    currentQuestionId,
    initializeDrawingSurface,
    isAssignment
  ])

  /**
   * Selects marker color for drawing unless interactions are disabled.
   */
  function handleMarkerColorSelect(color: 'red' | 'black') {
    if (enableStudentSubmissionView) return
    setSelectedMarkerColor(color)
  }

  /**
   * Standard keyboard activator for accessible interactive controls.
   * Triggers action on Enter and Space.
   */
  function onActivateWithKeyboard(
    event: React.KeyboardEvent,
    action: () => void
  ) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      action()
    }
  }

  /**
   * Closes marker popup and resets marker-related active state.
   */
  function closeMarkerPopup() {
    if (enableStudentSubmissionView) return

    if (isAssignment) {
      const currentMarkings = getMarkings() || []
      setIsScreenMarked(currentMarkings.length > 0)
      void saveStudentPenMarkings(currentMarkings)
    }

    setIsMarkerPopupOpen(false)
    unbindDrawing()
    unbindEraser()
    setActiveTool(null)
  }

  /**
   * Toggles highlighter tool and deactivates other conflicting tools.
   * Also handles transition out of calculator mode when required.
   */
  function toggleHighlighterTool() {
    if (enableStudentSubmissionView) return

    if (isHighlighterPopupOpen) {
      onDestroyHighlighter()
      onDestroyHighlighterRemover()
      setActiveTool(null)
      setIsHighlighterActive(false)
      setShowHighlightEraser(false)
      setIsHighlighterPopupOpen(false)
      return
    }

    if (isCalculatorOpen) closeCalculatorTool()

    setIsHighlighterActive(true)
    setIsMarkerPopupOpen(false)
    setIsContrastPopupOpen(false)
    setShowHighlightEraser(false)
    setIsHighlighterPopupOpen(true)
    setSelectedMarkerColor(null)
    unbindDrawing()
    unbindEraser()
    onDestroyHighlighterRemover()
    onClickHighLighter(setIsScreenMarked)
    setActiveTool('highlighter')
  }

  /**
   * Re-enables highlight mode from the highlighter popup.
   */
  function handleHighlighterModeClick() {
    if (enableStudentSubmissionView) return

    onDestroyHighlighterRemover()
    setShowHighlightEraser(false)
    onClickHighLighter(setIsScreenMarked)
    setIsHighlighterActive(true)
    setActiveTool('highlighter')
  }

  /**
   * Keyboard handler for highlighter mode selection.
   */
  function handleHighlighterModeKeyDown(event: React.KeyboardEvent) {
    onActivateWithKeyboard(event, handleHighlighterModeClick)
  }

  /**
   * Enables highlight eraser mode from popup controls.
   */
  function handleHighlighterEraserClick() {
    if (enableStudentSubmissionView || !isScreenMarked) return

    setShowHighlightEraser(!showHighlightEraser)
    setIsHighlighterActive(false)
    setActiveTool('highlighter')
  }

  /**
   * Keyboard handler for highlighter eraser selection.
   */
  function handleHighlighterEraserKeyDown(event: React.KeyboardEvent) {
    onActivateWithKeyboard(event, handleHighlighterEraserClick)
  }

  /**
   * Clears all highlighted text while keeping the highlighter popup open.
   */
  async function handleClearAllHighlightsClick() {
    const highlightArea = getHighlightArea()

    if (enableStudentSubmissionView || !highlightArea) return

    onDestroyHighlighter()
    onDestroyHighlighterRemover()

    const cleanupHighlighter = new (window.TextHighlighter as any)(
      highlightArea as HTMLElement,
      { isRemoveHighlight: false, setIsScreenMarked }
    )

    await cleanupHighlighter.removeHighlights()
    cleanupHighlighter.destroy()
    cacheCurrentQuestionHighlights()

    setIsScreenMarked(false)
    setShowHighlightEraser(false)
    onClickHighLighter(setIsScreenMarked)
    setIsHighlighterActive(true)
    setActiveTool('highlighter')
  }

  /**
   * Closes highlighter popup and resets highlighter state.
   */
  function closeHighlighterPopup() {
    if (enableStudentSubmissionView) return

    onDestroyHighlighter()
    onDestroyHighlighterRemover()
    setShowHighlightEraser(false)
    setIsHighlighterPopupOpen(false)
    setIsHighlighterActive(false)
    setActiveTool(null)
  }

  /**
   * Keyboard handler for highlighter popup close action.
   */
  function handleHighlighterRemoveKeyDown(event: React.KeyboardEvent) {
    onActivateWithKeyboard(event, closeHighlighterPopup)
  }

  /**
   * Opens or closes marker popup while turning off conflicting tool UIs.
   */
  function toggleMarkerPopup() {
    if (enableStudentSubmissionView) return
    if (isCalculatorOpen) closeCalculatorTool()
    onDestroyHighlighter()
    onDestroyHighlighterRemover()
    setIsHighlighterPopupOpen(false)
    setShowHighlightEraser(false)
    setIsHighlighterActive(false)
    setIsContrastPopupOpen(false)
    setSelectedMarkerColor((currentColor) => currentColor || 'red')
    setIsMarkerPopupOpen((prev) => {
      const next = !prev
      if (!next) {
        if (isAssignment) {
          const currentMarkings = getMarkings() || []
          setIsScreenMarked(currentMarkings.length > 0)
          void saveStudentPenMarkings(currentMarkings)
        }
        unbindDrawing()
        unbindEraser()
      }
      setActiveTool(next ? 'marker' : null)
      return next
    })
  }

  /**
   * Opens or closes contrast options popup and resets marker/highlighter state.
   */
  function toggleContrastPopup() {
    if (enableStudentSubmissionView) return
    if (isCalculatorOpen) closeCalculatorTool()
    // Reset states for other tools first
    onDestroyHighlighter()
    onDestroyHighlighterRemover()
    setIsHighlighterPopupOpen(false)
    setShowHighlightEraser(false)
    setIsMarkerPopupOpen(false)
    setIsHighlighterActive(false)
    setSelectedMarkerColor(null)
    unbindDrawing()
    unbindEraser()

    setIsContrastPopupOpen((prev) => {
      const next = !prev
      setActiveTool(next ? 'contrast' : null)
      return next
    })
  }

  /**
   * Mouse click handler for marker trigger control.
   */
  function handleMarkerTriggerClick() {
    toggleMarkerPopup()
  }

  /**
   * Keyboard handler for marker trigger control.
   */
  function handleMarkerTriggerKeyDown(event: React.KeyboardEvent) {
    onActivateWithKeyboard(event, toggleMarkerPopup)
  }

  /**
   * Mouse click handler for contrast trigger control.
   */
  function handleContrastTriggerClick() {
    toggleContrastPopup()
  }

  /**
   * Keyboard handler for contrast trigger control.
   */
  function handleContrastTriggerKeyDown(event: React.KeyboardEvent) {
    onActivateWithKeyboard(event, toggleContrastPopup)
  }

  /**
   * Mouse click handler for highlighter trigger control.
   */
  function handleHighlighterTriggerClick() {
    toggleHighlighterTool()
  }

  /**
   * Keyboard handler for highlighter trigger control.
   */
  function handleHighlighterTriggerKeyDown(event: React.KeyboardEvent) {
    onActivateWithKeyboard(event, toggleHighlighterTool)
  }

  /**
   * Selects the red marker variant.
   */
  function handleRedMarkerClick() {
    handleMarkerColorSelect('red')
  }

  /**
   * Keyboard handler for red marker selection.
   */
  function handleRedMarkerKeyDown(event: React.KeyboardEvent) {
    onActivateWithKeyboard(event, handleRedMarkerClick)
  }

  /**
   * Selects the black marker variant.
   */
  function handleBlackMarkerClick() {
    handleMarkerColorSelect('black')
  }

  /**
   * Keyboard handler for black marker selection.
   */
  function handleBlackMarkerKeyDown(event: React.KeyboardEvent) {
    onActivateWithKeyboard(event, handleBlackMarkerClick)
  }

  /**
   * Enables eraser interaction mode for marker paths.
   */
  function handleEraserClick() {
    if (enableStudentSubmissionView) return
    unbindDrawing()
    bindEraser()
    setSelectedMarkerColor(null)
    document.addEventListener('click', handler)
  }

  function handleClearAllMarkers() {
    if (enableStudentSubmissionView) return

    eraseDrawing()
    setIsScreenMarked(false)

    if (!isAssignment && currentQuestionId) {
      markerMarkingsByQuestionRef.current[currentQuestionId] = []
    }

    if (isAssignment && currentPage) {
      const classroomId = currentClassroom?.id
      const assignmentId = assignment?.id
      const pageId = currentPage?.id

      markerMarkingsByPageRef.current[currentPage.id] = []
      lastSavedMarkerMarkingsRef.current = JSON.stringify([])

      dispatch(
        updateAssignmentPageAction({
          ...currentPage,
          student_pen_markings: []
        })
      )

      if (classroomId && assignmentId && pageId) {
        upsertAssignmentAnnotations(classroomId, assignmentId, {
          page_id: pageId,
          is_bookmarked: Boolean(currentPage?.is_bookmarked),
          student_pen_markings: JSON.stringify([])
        }).catch((error) => {
          console.error('Failed to clear assignment marker annotations', error)
        })
      }
    }

    scheduleStudentPenMarkingsSave([])
  }

  /**
   * Keyboard handler for eraser selection.
   */
  function handleEraserKeyDown(event: React.KeyboardEvent) {
    onActivateWithKeyboard(event, handleEraserClick)
  }

  /**
   * Closes marker popup from remove icon action.
   */
  function handleRemoveClick() {
    closeMarkerPopup()
  }

  /**
   * Keyboard handler for marker popup close icon.
   */
  function handleRemoveKeyDown(event: React.KeyboardEvent) {
    onActivateWithKeyboard(event, handleRemoveClick)
  }

  /**
   * Opens student notes modal.
   */
  function openNotesModal() {
    setNotesDraft(notesHtml)
    setIsNotesModalOpen(true)
  }

  /**
   * Closes student notes modal.
   */
  async function closeNotesModal(penPayload?: any) {
    const nextNotes = notesDraft

    if (assessmentType === 'assignments') {
      const classroomId = currentClassroom?.id
      const assignmentId = assignment?.id
      const pageId = currentPage?.id

      if (classroomId && assignmentId && pageId) {
        const previousNotes = currentPage?.student_notes || ''
        const previousPenMarkings =
          currentPage?.student_pen_note_markings || null
        const nextPenMarkings = penPayload || {
          version: 1,
          created_at: new Date().toISOString(),
          markings: []
        }
        const didPenMarkingsChange =
          JSON.stringify(nextPenMarkings || null) !==
          JSON.stringify(previousPenMarkings || null)

        if (nextNotes !== previousNotes || didPenMarkingsChange) {
          dispatch(
            updateAssignmentPageAction({
              ...currentPage,
              student_notes: nextNotes,
              student_pen_note_markings: nextPenMarkings
            })
          )

          try {
            const serializedPenMarkings = JSON.stringify(
              nextPenMarkings || null
            )

            await upsertAssignmentAnnotations(classroomId, assignmentId, {
              page_id: pageId,
              is_bookmarked: Boolean(currentPage?.is_bookmarked),
              student_notes: nextNotes,
              student_pen_note_markings: serializedPenMarkings
            })
          } catch (error) {
            dispatch(updateAssignmentPageAction(currentPage))
            console.error('Failed to save assignment student notes', error)
          }
        }
      }
    } else {
      const previousNotes = (questions[questionIdx] as any)?.student_notes || ''

      if (nextNotes !== previousNotes) {
        onChange('student_notes', nextNotes)
      }
    }

    setIsNotesModalOpen(false)
  }

  /**
   * Resolves the visible calculator wrapper element.
   * If a shared overlay wrapper exists, it is used as visibility target.
   */
  function getCalculatorOverlayElement(calculatorElement: HTMLElement) {
    const overlayElement = calculatorElement.closest(
      '[data-calculator-overlay="true"]'
    ) as HTMLElement | null

    return overlayElement || calculatorElement
  }

  /**
   * The TI calculator needs the vendor container structure around the mount
   * node, so this prepares the overlay host before the TI runtime is created.
   */
  function prepareTiCalculatorHost(calculatorElement: HTMLElement) {
    calculatorElement.innerHTML = ''

    const calculatorContainer = document.createElement('div')
    calculatorContainer.id = 'calculatorContainer'

    const calculatorMountElement = document.createElement('div')
    calculatorMountElement.id = TI84_CALCULATOR_MOUNT_ID

    const calculatorCopyright = document.createElement('div')
    calculatorCopyright.id = 'sideCopyright'
    calculatorCopyright.className = 'ti84pceCopyrightSize'

    /**
     * The TI vendor markup expects its copyright block to stay in the same
     * container structure as the teacher portal so the footer text renders.
     */
    const copyrightText = document.createElement('span')
    copyrightText.className = 'copyrightText'
    copyrightText.textContent =
      'Copyright © 2013 - 2025 Texas Instruments Incorporated. All rights reserved.'

    calculatorCopyright.appendChild(copyrightText)
    calculatorContainer.appendChild(calculatorMountElement)
    calculatorContainer.appendChild(calculatorCopyright)
    calculatorElement.appendChild(calculatorContainer)
  }

  /**
   * The calculator overlay can host either Desmos or TI. Closing needs to
   * clear whichever runtime is active before the overlay is hidden.
   */
  const destroyActiveCalculator = useCallback(() => {
    const activeCalculator = getCalculator()
    const calculatorElement = getCalculatorElement()

    if (!calculatorElement) {
      setCalculator(null)
      return
    }

    if ((activeCalculator as TiCalculatorInstance | null)?.killInstance) {
      destroyTiCalculator(
        activeCalculator as TiCalculatorInstance | null,
        calculatorElement as HTMLDivElement
      )
      calculatorElement.innerHTML = ''
      setCalculator(null)
      return
    }

    activeCalculator && activeCalculator.destroy()
    setCalculator(null)
    calculatorElement.innerHTML = ''
  }, [getCalculatorElement])

  /**
   * Fully closes calculator mode by destroying the instance and hiding
   * both the overlay wrapper and inner calculator mount element.
   */
  const closeCalculatorTool = useCallback(() => {
    const calculatorElement = getCalculatorElement()
    if (calculatorElement) {
      destroyActiveCalculator()
      const overlayElement = getCalculatorOverlayElement(calculatorElement)
      overlayElement.style.display = 'none'
      calculatorElement.style.display = 'none'
      calculatorElement.style.width = ''
      calculatorElement.style.height = ''
      calculatorElement.style.maxHeight = ''
    }
  }, [destroyActiveCalculator, getCalculatorElement])

  /**
   * Global escape shortcut that clears any selected tool.
   */
  useEffect(() => {
    const onEscapePress = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return

      if (
        activeTool ||
        isHighlighterPopupOpen ||
        isMarkerPopupOpen ||
        isContrastPopupOpen ||
        isCalculatorPopupOpen ||
        selectedMarkerColor
      ) {
        onDestroyHighlighter()
        onDestroyHighlighterRemover()
        setIsHighlighterActive(false)
        setShowHighlightEraser(false)
        setIsHighlighterPopupOpen(false)
        setIsMarkerPopupOpen(false)
        setIsContrastPopupOpen(false)
        setIsCalculatorPopupOpen(false)
        setSelectedMarkerColor(null)
        unbindDrawing()
        unbindEraser()
        document.removeEventListener('click', handler)

        if (activeTool === 'calculator') {
          closeCalculatorTool()
        }

        setActiveTool(null)
      }
    }

    window.addEventListener('keydown', onEscapePress)

    return () => {
      window.removeEventListener('keydown', onEscapePress)
    }
  }, [
    activeTool,
    isMarkerPopupOpen,
    isContrastPopupOpen,
    isCalculatorPopupOpen,
    selectedMarkerColor,
    isHighlighterPopupOpen,
    setIsHighlighterActive,
    setShowHighlightEraser,
    handler,
    closeCalculatorTool,
    onDestroyHighlighter,
    onDestroyHighlighterRemover
  ])

  /**
   * Mirrors V1 behavior: eraser mode binds a separate highlighter remover,
   * while highlight mode destroys that remover.
   */
  useEffect(() => {
    if (showHighlightEraser) {
      onDestroyHighlighter()
      onClickHighlighterEraser(setIsScreenMarked)
      return
    }

    onDestroyHighlighterRemover()
  }, [
    showHighlightEraser,
    onClickHighlighterEraser,
    onDestroyHighlighter,
    onDestroyHighlighterRemover,
    setIsScreenMarked
  ])
  /**
   * Keyboard handler for notes trigger button.
   */
  function handleNotesTriggerKeyDown(event: React.KeyboardEvent) {
    onActivateWithKeyboard(event, openNotesModal)
  }

  /**
   * Applies selected contrast mode and closes the contrast popup.
   */
  function handleContrastOptionSelect(optionId: ContrastMode) {
    if (enableStudentSubmissionView) return

    dispatch(setThemeContrast(optionId))
    updateStudentContrastPreset({
      contrast_preset: optionId.toUpperCase() as Uppercase<ContrastMode>
    })
      .unwrap()
      .catch((error) => {
        console.error('Failed to update student contrast preset', error)
      })

    setIsContrastPopupOpen(false)
    setActiveTool(null)
  }

  /**
   * Ensures Desmos script is available before calculator initialization.
   * Supports already-loaded, already-in-DOM, and fresh-injection scenarios.
   */
  function ensureCalculatorScriptLoaded() {
    if ((window as any).Desmos) return Promise.resolve(true)

    const existingScript = document.getElementById(
      'desmos-calc'
    ) as HTMLScriptElement | null

    if (existingScript) {
      return new Promise<boolean>((resolve) => {
        const onLoad = () => {
          existingScript.setAttribute('data-loaded', 'true')
          resolve(true)
        }

        const onError = () => resolve(false)

        existingScript.addEventListener('load', onLoad, { once: true })
        existingScript.addEventListener('error', onError, { once: true })
      })
    }

    return new Promise<boolean>((resolve) => {
      const calculatorScript = document.createElement('script')
      calculatorScript.id = 'desmos-calc'
      calculatorScript.src = '/calculator.js'
      calculatorScript.async = true

      calculatorScript.onload = () => {
        calculatorScript.setAttribute('data-loaded', 'true')
        resolve(true)
      }
      calculatorScript.onerror = () => resolve(false)

      document.head.appendChild(calculatorScript)
    })
  }

  /**
   * Opens the existing Desmos overlay flow and keeps the selected calculator
   * mode in local state so repeated toggles can close the correct runtime.
   */
  async function openDesmosCalculatorTool() {
    const calculatorElement = getCalculatorElement()
    if (!calculatorElement) return

    const isScriptLoaded = await ensureCalculatorScriptLoaded()
    if (!isScriptLoaded) return

    const overlayElement = getCalculatorOverlayElement(calculatorElement)

    calculatorElement.innerHTML = ''
    overlayElement.style.display = 'block'
    calculatorElement.style.display = 'block'
    calculatorElement.style.width = '400px'
    calculatorElement.style.height = '600px'

    const calculator = showDesmosCalculator(
      grade,
      calculatorElement as HTMLElement
    )

    if (!calculator) {
      overlayElement.style.display = 'none'
      setActiveTool(null)
      return
    }

    setCalculator(calculator)
    setActiveTool('calculator')

    requestAnimationFrame(() => {
      calculatorElement.style.height = 'auto'
      calculatorElement.style.maxHeight = '650px'
      if (calculator.resize) {
        calculator.resize()
      }
    })
  }

  /**
   * Opens the TI calculator inside the shared floating overlay so the student
   * tools UI can reuse the same close and drag behavior as Desmos.
   */
  async function openTiCalculatorTool() {
    const calculatorElement = getCalculatorElement()
    if (!calculatorElement) return

    const overlayElement = getCalculatorOverlayElement(calculatorElement)

    prepareTiCalculatorHost(calculatorElement as HTMLElement)
    overlayElement.style.display = 'block'
    calculatorElement.style.display = 'block'
    calculatorElement.style.width = 'auto'
    calculatorElement.style.height = 'auto'
    calculatorElement.style.maxHeight = ''

    try {
      const calculatorInstance = await showTiCalculator(
        TI84_CALCULATOR_MOUNT_ID
      )

      setCalculator(calculatorInstance)
      setActiveTool('calculator')

      /**
       * The TI runtime renders its own intrinsic height, so the shared overlay
       * should fall back to content sizing instead of keeping the fixed Desmos
       * height that leaves empty space below the calculator.
       */
      requestAnimationFrame(() => {
        calculatorElement.style.height = 'auto'
        calculatorElement.style.maxHeight = ''
      })
    } catch {
      overlayElement.style.display = 'none'
      calculatorElement.style.display = 'none'
      calculatorElement.innerHTML = ''
      setActiveTool(null)
    }
  }

  /**
   * Toggles calculator tool state.
   * On open: disables conflicting tools, ensures script is loaded,
   * shows overlay, mounts Desmos calculator by grade.
   * On close: destroys and hides calculator.
   */
  async function toggleCalculator() {
    if (enableStudentSubmissionView) return

    if (isCalculatorOpen) {
      closeCalculatorTool()
      setIsCalculatorPopupOpen(false)
      setActiveTool(null)
      return
    }

    onDestroyHighlighter()
    onDestroyHighlighterRemover()
    setIsHighlighterActive(false)
    setIsHighlighterPopupOpen(false)
    setShowHighlightEraser(false)
    unbindDrawing()
    unbindEraser()
    setSelectedMarkerColor(null)
    setIsMarkerPopupOpen(false)
    setIsContrastPopupOpen(false)
    setIsCalculatorPopupOpen(shouldShowCalculatorChoicePopup)

    if (shouldShowCalculatorChoicePopup) {
      return
    }

    if (isGraphingCalculatorAllowed) {
      await openTiCalculatorTool()
      return
    }

    await openDesmosCalculatorTool()
  }

  /**
   * Keyboard handler for calculator trigger button.
   */
  function handleCalculatorTriggerKeyDown(event: React.KeyboardEvent) {
    onActivateWithKeyboard(event, toggleCalculator)
  }

  /**
   * Keeps tool state consistent when visibility props are toggled off.
   * Hidden tools are force-closed and cleaned up if they were active.
   */
  useEffect(() => {
    if (!highlighterVisible && activeTool === 'highlighter') {
      onDestroyHighlighter()
      onDestroyHighlighterRemover()
      setIsHighlighterPopupOpen(false)
      setShowHighlightEraser(false)
      setIsHighlighterActive(false)
      setActiveTool(null)
    }

    if (!markerVisible) {
      if (isMarkerPopupOpen || activeTool === 'marker' || selectedMarkerColor) {
        if (isAssignment) {
          const currentMarkings = getMarkings() || []
          setIsScreenMarked(currentMarkings.length > 0)
          void saveStudentPenMarkings(currentMarkings)
        }
        setIsMarkerPopupOpen(false)
        setSelectedMarkerColor(null)
        unbindDrawing()
        unbindEraser()
        document.removeEventListener('click', handler)
        if (activeTool === 'marker') {
          setActiveTool(null)
        }
      }
    }

    if (
      !contrastVisible &&
      (isContrastPopupOpen || activeTool === 'contrast')
    ) {
      setIsContrastPopupOpen(false)
      if (activeTool === 'contrast') {
        setActiveTool(null)
      }
    }

    if (!calculatorVisible) {
      if (activeTool === 'calculator') {
        closeCalculatorTool()
        setActiveTool(null)
      }

      if (isCalculatorPopupOpen) {
        setIsCalculatorPopupOpen(false)
      }
    }

    if (!notesVisible && isNotesModalOpen) {
      setIsNotesModalOpen(false)
    }
  }, [
    activeTool,
    calculatorVisible,
    closeCalculatorTool,
    contrastVisible,
    handler,
    highlighterVisible,
    isAssignment,
    isContrastPopupOpen,
    isCalculatorPopupOpen,
    isMarkerPopupOpen,
    isNotesModalOpen,
    markerVisible,
    notesVisible,
    onDestroyHighlighter,
    onDestroyHighlighterRemover,
    saveStudentPenMarkings,
    selectedMarkerColor,
    setIsHighlighterActive,
    setShowHighlightEraser
  ])

  return (
    <FooterIconContainer>
      {/** Highlighter toggle tool button. */}
      {highlighterVisible && (
        <HighlighterPopupWrapper ref={highlighterPopupRef}>
          <MarkerTrigger
            $disabled={enableStudentSubmissionView}
            onClick={handleHighlighterTriggerClick}
            onKeyDown={handleHighlighterTriggerKeyDown}
            role='button'
            tabIndex={interactiveTabIndex}
            aria-label='Highlighter options'
            aria-haspopup='dialog'
            aria-expanded={isHighlighterPopupOpen}
            aria-controls='student-tools-highlighter-popup'
            aria-disabled={enableStudentSubmissionView}
          >
            <SelectionBox
              isActive={isHighlighterPopupOpen && activeTool === 'highlighter'}
            >
              <FooterHighlighterIcon
                $disabled={enableStudentSubmissionView}
                aria-hidden='true'
              />
            </SelectionBox>
          </MarkerTrigger>

          {isHighlighterPopupOpen && (
            <HighlighterPopupContainer
              id='student-tools-highlighter-popup'
              role='dialog'
              aria-label='Highlighter options'
              aria-modal='false'
            >
              <PopupHighlighterIcon
                $disabled={enableStudentSubmissionView}
                $isFaded={showHighlightEraser}
                onClick={handleHighlighterModeClick}
                onKeyDown={handleHighlighterModeKeyDown}
                role='button'
                tabIndex={interactiveTabIndex}
                aria-disabled={enableStudentSubmissionView}
                aria-pressed={!showHighlightEraser}
                aria-label='Select highlighter'
              />

              <PopupEraserIcon
                $disabled={!isScreenMarked}
                onClick={handleHighlighterEraserClick}
                onKeyDown={handleHighlighterEraserKeyDown}
                role='button'
                tabIndex={interactiveTabIndex}
                aria-disabled={!isScreenMarked || enableStudentSubmissionView}
                aria-pressed={showHighlightEraser}
                aria-label='Select highlight eraser'
              />

              <ClearAllButton onClick={handleClearAllHighlightsClick}>
                Clear All
              </ClearAllButton>

              <PopupRemoveIcon
                $disabled={enableStudentSubmissionView}
                onClick={closeHighlighterPopup}
                onKeyDown={handleHighlighterRemoveKeyDown}
                role='button'
                tabIndex={interactiveTabIndex}
                aria-disabled={enableStudentSubmissionView}
                aria-label='Close highlighter options'
              />
            </HighlighterPopupContainer>
          )}
        </HighlighterPopupWrapper>
      )}

      {/** Marker tool trigger and marker options popup. */}
      {markerVisible && (
        <MarkerPopupWrapper ref={markerPopupRef}>
          <MarkerTrigger
            $disabled={enableStudentSubmissionView}
            ref={markerRef}
            onClick={handleMarkerTriggerClick}
            onKeyDown={handleMarkerTriggerKeyDown}
            role='button'
            tabIndex={interactiveTabIndex}
            aria-label='Marker options'
            aria-haspopup='dialog'
            aria-expanded={isMarkerPopupOpen}
            aria-controls='student-tools-marker-popup'
            aria-disabled={enableStudentSubmissionView}
          >
            <SelectionBox
              isActive={isMarkerPopupOpen && activeTool === 'marker'}
            >
              <MarkerIcon />
            </SelectionBox>
          </MarkerTrigger>

          {isMarkerPopupOpen && (
            <MarkerPopupContainer
              id='student-tools-marker-popup'
              role='dialog'
              aria-label='Marker options'
              aria-modal='false'
            >
              <PopupRedDotIcon
                $disabled={enableStudentSubmissionView}
                $isFaded={isBlackSelected}
                onClick={handleRedMarkerClick}
                onKeyDown={handleRedMarkerKeyDown}
                role='button'
                tabIndex={interactiveTabIndex}
                aria-disabled={enableStudentSubmissionView}
                aria-pressed={isRedSelected}
                aria-label='Select red marker'
              />

              <PopupBlackDotIcon
                $disabled={enableStudentSubmissionView}
                $isFaded={isRedSelected}
                onClick={handleBlackMarkerClick}
                onKeyDown={handleBlackMarkerKeyDown}
                role='button'
                tabIndex={interactiveTabIndex}
                aria-disabled={enableStudentSubmissionView}
                aria-pressed={isBlackSelected}
                aria-label='Select black marker'
              />

              <PopupEraserIcon
                $disabled={!isScreenMarked}
                onClick={handleEraserClick}
                ref={eraserRef}
                onKeyDown={handleEraserKeyDown}
                role='button'
                tabIndex={interactiveTabIndex}
                aria-disabled={enableStudentSubmissionView}
                aria-label='Select eraser'
              />

              <ClearAllButton onClick={handleClearAllMarkers}>
                Clear All
              </ClearAllButton>
              <PopupRemoveIcon
                $disabled={enableStudentSubmissionView}
                onClick={handleRemoveClick}
                onKeyDown={handleRemoveKeyDown}
                role='button'
                tabIndex={interactiveTabIndex}
                aria-disabled={enableStudentSubmissionView}
                aria-label='Close marker options'
              />
            </MarkerPopupContainer>
          )}
        </MarkerPopupWrapper>
      )}

      {/** Contrast tool trigger and contrast palette popup. */}
      {contrastVisible && (
        <ContrastPopupWrapper ref={contrastPopupRef}>
          <ContrastTrigger
            type='button'
            $disabled={enableStudentSubmissionView}
            onClick={handleContrastTriggerClick}
            onKeyDown={handleContrastTriggerKeyDown}
            tabIndex={interactiveTabIndex}
            aria-label='Contrast options'
            aria-haspopup='dialog'
            aria-expanded={isContrastPopupOpen}
            aria-controls='student-tools-contrast-popup'
            aria-disabled={enableStudentSubmissionView}
          >
            <SelectionBox
              isActive={isContrastPopupOpen && activeTool === 'contrast'}
            >
              <FooterContrastIcon
                $disabled={enableStudentSubmissionView}
                aria-hidden='true'
              />
            </SelectionBox>
          </ContrastTrigger>

          {isContrastPopupOpen && (
            <ContrastPopupContainer
              id='student-tools-contrast-popup'
              role='dialog'
              aria-label='Contrast options'
              aria-modal='false'
            >
              <ContrastCardGrid>
                {CONTRAST_OPTIONS.map((option) => {
                  const isSelected = selectedContrastId === option.id
                  const contrastColors =
                    contrastColorsByMode[option.id] ||
                    contrastColorsByMode[DEFAULT_CONTRAST_MODE]

                  return (
                    <ContrastCard
                      $isSelected={isSelected}
                      style={{
                        backgroundColor: contrastColors.background.primary,
                        color: contrastColors.text.primary
                      }}
                      key={option.id}
                      as='button'
                      onClick={() => handleContrastOptionSelect(option.id)}
                      onKeyDown={(event) =>
                        onActivateWithKeyboard(event, () =>
                          handleContrastOptionSelect(option.id)
                        )
                      }
                      tabIndex={interactiveTabIndex}
                      aria-label={option.label}
                      aria-pressed={isSelected}
                      aria-disabled={enableStudentSubmissionView}
                    >
                      {option.label}
                    </ContrastCard>
                  )
                })}
              </ContrastCardGrid>
            </ContrastPopupContainer>
          )}
        </ContrastPopupWrapper>
      )}

      {/** Calculator trigger shown only for math-enabled assessments. */}
      {allowCalculator && calculatorVisible && (
        <CalculatorPopupWrapper ref={calculatorPopupRef}>
          <CalculatorTrigger
            type='button'
            $disabled={enableStudentSubmissionView}
            onClick={toggleCalculator}
            onKeyDown={handleCalculatorTriggerKeyDown}
            tabIndex={interactiveTabIndex}
            aria-label='Calculator'
            aria-pressed={isCalculatorOpen || isCalculatorPopupOpen}
            aria-haspopup={
              shouldShowCalculatorChoicePopup ? 'dialog' : undefined
            }
            aria-expanded={
              shouldShowCalculatorChoicePopup
                ? isCalculatorPopupOpen
                : undefined
            }
            aria-disabled={enableStudentSubmissionView}
          >
            <SelectionBox isActive={isCalculatorOpen || isCalculatorPopupOpen}>
              <FooterCalculatorIcon
                $disabled={enableStudentSubmissionView}
                aria-hidden='true'
              />
            </SelectionBox>
          </CalculatorTrigger>

          {shouldShowCalculatorChoicePopup && isCalculatorPopupOpen && (
            <CalculatorPopupContainer
              role='dialog'
              aria-label='Calculator options'
              aria-modal='false'
            >
              <CalculatorOptionButton
                type='button'
                onClick={() => {
                  setIsCalculatorPopupOpen(false)
                  openDesmosCalculatorTool()
                }}
              >
                {standardCalculatorLabel}
              </CalculatorOptionButton>

              <CalculatorOptionButton
                type='button'
                onClick={() => {
                  setIsCalculatorPopupOpen(false)
                  openTiCalculatorTool()
                }}
              >
                Graphing Calculator
              </CalculatorOptionButton>
            </CalculatorPopupContainer>
          )}
        </CalculatorPopupWrapper>
      )}

      {/** Student notes modal trigger. */}
      {notesVisible && (
        <NotesTrigger
          type='button'
          // $disabled={isTeacherMode}
          onClick={openNotesModal}
          onKeyDown={handleNotesTriggerKeyDown}
          aria-label='Open student notes modal'
        >
          <FooterNotesIcon aria-hidden='true' />
        </NotesTrigger>
      )}

      {/** Notes modal host component. */}
      <StudentNotesModal
        visible={isNotesModalOpen}
        contextLabel={notesContextLabel}
        subtitle={notesSubtitle}
        notesHtml={notesDraft}
        initialPenMarkings={notesPenMarkings}
        onNotesChange={(value) => {
          setNotesDraft(value)
        }}
        onClose={closeNotesModal}
      />
    </FooterIconContainer>
  )
}

export default StudentTools
